import { StyleSheet, css } from 'aphrodite'
import type React from 'react'
import { useEffect, useState } from 'react'
import { projectService } from '../../services/projectService'
import { workOrderService } from '../../services/workOrderService'
import type { Project } from '../../types/project'
import type { WorkOrder, WorkOrderStatus } from '../../types/workorder'
import { formatFullDateTime } from '../../utils/dateUtils'
import Modal from '../Modal'
import StatusBadge from '../StatusBadge'
import WorkOrderConfirmation from './WorkOrderConfirmation'

interface ViewWorkOrderModalProps {
  workOrder: WorkOrder
  onClose: () => void
}

const ViewWorkOrderModal: React.FC<ViewWorkOrderModalProps> = ({
  workOrder,
  onClose,
}) => {
  const [project, setProject] = useState<Project | null>(null)

  useEffect(() => {
    const fetchProject = async () => {
      try {
        // Set project data - either from workOrder.project if it's already an object or fetch it
        if (typeof workOrder.project === 'object') {
          setProject(workOrder.project)
        } else if (typeof workOrder.project === 'number') {
          const projectData = await projectService.getProject(
            workOrder.project.toString(),
          )
          setProject(projectData)
        }
      } catch (error) {
        console.error('Failed to fetch project:', error)
      }
    }

    fetchProject()
  }, [workOrder.project])

  const handleDownloadPdf = async () => {
    try {
      const workOrderId = workOrder.id || workOrder.label
      if (!workOrderId) return
      const blob = await workOrderService.downloadWorkOrderPdf(workOrderId)
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `dispatch_${workOrder.label}.pdf`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Failed to download PDF:', error)
    }
  }

  return (
    <Modal onClose={onClose}>
      <div className={css(styles.modal)}>
        <div className={css(styles.header)}>
          <button
            type="button"
            onClick={onClose}
            className={css(styles.closeButton)}
          >
            <i className="fa-solid fa-times" />
          </button>
          <div className={css(styles.titleRow)}>
            <h2 className={css(styles.title)}>
              {workOrder.status === 'ACCEPTED'
                ? 'Dispatch – Accepted'
                : workOrder.status === 'COMPLETE'
                  ? 'Dispatch – Completed'
                  : workOrder.status === 'REJECTED'
                    ? 'Dispatch – Declined'
                    : 'Dispatch – Pending'}
            </h2>
            <StatusBadge status={workOrder.status as WorkOrderStatus} />
          </div>
          <div className={css(styles.subHeaderRow)}>
            <div className={css(styles.poNumber)}>
              PID: {project?.label || 'None'}
            </div>
            {workOrder.label && (
              <span className={css(styles.woLabel)}>
                WOID: {workOrder.label}
              </span>
            )}
          </div>
        </div>
        <div className={css(styles.content)}>
          <WorkOrderConfirmation data={workOrder} />

          {/* Decline Information Section */}
          {workOrder.status === 'REJECTED' && workOrder.decline_reason && (
            <div className={css(styles.declineSection)}>
              <h4 className={css(styles.declineTitle)}>Decline Reason:</h4>
              <p className={css(styles.declineReason)}>
                {workOrder.decline_reason}
              </p>
              {(workOrder.declined_by || workOrder.declined_by_first_name) && (
                <p className={css(styles.declinedBy)}>
                  Declined by{' '}
                  {/* Prefer snapshot, fallback to FK for backward compatibility */}
                  {workOrder.declined_by_first_name
                    ? `${workOrder.declined_by_first_name} ${workOrder.declined_by_last_name || ''}`.trim() ||
                      workOrder.declined_by_email
                    : `${workOrder.declined_by?.first_name || ''} ${workOrder.declined_by?.last_name || ''}`.trim() ||
                      workOrder.declined_by?.email}
                  {workOrder.declined_at &&
                    ` on ${formatFullDateTime(workOrder.declined_at)}`}
                </p>
              )}
            </div>
          )}
        </div>
        <div className={css(styles.footer)}>
          {(workOrder.status === 'ACCEPTED' ||
            workOrder.status === 'COMPLETE') && (
            <button
              onClick={handleDownloadPdf}
              className={css(styles.downloadBtn)}
            >
              <i className="fa-solid fa-download" /> Download PDF
            </button>
          )}
          <button onClick={onClose} className={css(styles.closeBtn)}>
            Close
          </button>
        </div>
      </div>
    </Modal>
  )
}

const styles = StyleSheet.create({
  modal: {
    backgroundColor: 'white',
    borderRadius: '8px',
    width: '100%',
    maxWidth: '800px',
    display: 'flex',
    flexDirection: 'column',
    maxHeight: '90vh',
  },
  header: {
    padding: '24px',
    borderBottom: '1px solid #E5E7EB',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  subHeaderRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  title: {
    margin: 0,
    fontSize: '18px',
    fontWeight: 600,
    color: '#111827',
  },
  poNumber: {
    fontSize: '12px',
    color: '#6B7280',
  },
  woLabel: {
    fontSize: '12px',
    color: '#6B7280',
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '16px',
    color: '#6B7280',
    cursor: 'pointer',
    padding: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'flex-end',
    ':hover': {
      color: '#1F2937',
    },
  },
  content: {
    padding: '24px',
    flex: 1,
    overflowY: 'auto',
    minHeight: 0,
  },

  footer: {
    padding: '24px',
    borderTop: '1px solid #E5E7EB',
    display: 'flex',
    justifyContent: 'flex-end',
  },
  closeBtn: {
    backgroundColor: '#6B7280',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    padding: '8px 16px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 500,
    ':hover': {
      backgroundColor: '#4B5563',
    },
  },
  downloadBtn: {
    backgroundColor: '#10B981',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    padding: '8px 16px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 500,
    marginRight: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    ':hover': {
      backgroundColor: '#059669',
    },
  },
  declineSection: {
    marginTop: '24px',
    backgroundColor: '#FFFBEB',
    border: '1px solid #F59E0B',
    borderRadius: '8px',
    padding: '16px',
  },
  declineTitle: {
    margin: 0,
    marginBottom: '8px',
    fontSize: '14px',
    fontWeight: 600,
    color: '#92400E',
  },
  declineReason: {
    margin: 0,
    marginBottom: '12px',
    fontSize: '14px',
    color: '#78350F',
    lineHeight: 1.5,
    whiteSpace: 'pre-wrap',
  },
  declinedBy: {
    margin: 0,
    fontSize: '12px',
    color: '#B45309',
    fontStyle: 'italic',
  },
})

export default ViewWorkOrderModal
